/**
 * The delegated-authority vocabulary for the MANAGER persona (מנהל) — a company user who sits above
 * a trainer and up to, but never above, a company admin.
 *
 * <p>Mirrors the server's `Permission` enum. The names are the contract: `/user/me` returns
 * `capabilities` as these strings, and `@PreAuthorize("@caps.can('…')")` reads the same ones.
 *
 * <p>The list is deliberately presented as "which company-admin abilities does this manager get?",
 * but nothing here grants anything on its own — the server is default-deny and is the boundary.
 * Hiding UI for an ungranted capability is presentation, not enforcement.
 *
 * <p><b>Every constant here becomes a checkbox</b> in the create/edit manager dialogs, via
 * {@link ALL_CAPABILITIES}. The two label maps below are typed `Record<Capability, string>`, so
 * adding a constant without its Hebrew label and description fails `npm run typecheck:app` rather
 * than rendering a blank row — which is the whole reason they are exhaustive records and not
 * partials with a fallback.
 */
export const CAPABILITIES = {
  MANAGE_TRAINERS: "MANAGE_TRAINERS",
  MANAGE_EXERCISES: "MANAGE_EXERCISES",
  MANAGE_TEMPLATES: "MANAGE_TEMPLATES",
  MANAGE_GUIDES: "MANAGE_GUIDES",
  MANAGE_RECIPES: "MANAGE_RECIPES",
  MANAGE_SUPPLEMENTS: "MANAGE_SUPPLEMENTS",
  MANAGE_MEASUREMENTS: "MANAGE_MEASUREMENTS",
  MANAGE_FORMS: "MANAGE_FORMS",
  MANAGE_MENU_TEMPLATES: "MANAGE_MENU_TEMPLATES",
  MANAGE_FOOD: "MANAGE_FOOD",
  MANAGE_NOTIFICATIONS: "MANAGE_NOTIFICATIONS",
  MANAGE_AUTOMATIONS: "MANAGE_AUTOMATIONS",
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

/** Hebrew labels for the create/edit permission pickers. */
export const CAPABILITY_LABELS: Record<Capability, string> = {
  MANAGE_TRAINERS: "ניהול מאמנים",
  MANAGE_EXERCISES: "ניהול תרגילים",
  MANAGE_TEMPLATES: "ניהול תבניות אימון",
  MANAGE_GUIDES: "ניהול מדריכים",
  MANAGE_RECIPES: "ניהול מתכונים",
  MANAGE_SUPPLEMENTS: "ניהול תוספים",
  MANAGE_MEASUREMENTS: "ניהול מדידות",
  MANAGE_FORMS: "ניהול טפסים",
  MANAGE_MENU_TEMPLATES: "ניהול תפריטי תזונה",
  MANAGE_FOOD: "ניהול מאגר מזון",
  MANAGE_NOTIFICATIONS: "ניהול התראות",
  MANAGE_AUTOMATIONS: "ניהול אוטומציות",
};

/** One-line explanation shown under each toggle, so an admin knows what they are handing over. */
export const CAPABILITY_DESCRIPTIONS: Record<Capability, string> = {
  MANAGE_TRAINERS:
    "יצירה, עריכה, הפעלה, השבתה ומחיקה של מאמנים — רק אלה שבתחום האחריות שלו",
  MANAGE_EXERCISES:
    "יצירה, עריכה ומחיקה של ספריית התרגילים של החברה",
  MANAGE_TEMPLATES:
    "יצירה, עריכה, שכפול ומחיקה של תבניות האימון של החברה",
  MANAGE_GUIDES:
    "יצירה, עריכה ומחיקה של המדריכים של החברה, כולל התגיות והקטגוריות שלהם",
  MANAGE_RECIPES:
    "יצירה, עריכה ומחיקה של המתכונים של החברה, כולל התגיות והקטגוריות שלהם",
  MANAGE_SUPPLEMENTS:
    "יצירה, עריכה ומחיקה של קטלוג התוספים של החברה",
  MANAGE_MEASUREMENTS:
    "ניהול סוגי המדידות של החברה — לא הערכים שנרשמו למתאמנים",
  MANAGE_FORMS:
    "יצירה, עריכה ומחיקה של תבניות הטפסים — שליחת טופס למתאמן שייכת לניהול מאמנים",
  MANAGE_MENU_TEMPLATES:
    "יצירה, עריכה, שכפול ומחיקה של תפריטי התזונה הכלליים של החברה",
  MANAGE_FOOD:
    "יצירה, עריכה ומחיקה של פריטי המזון של החברה וגדלי המנות שלהם",
  MANAGE_NOTIFICATIONS:
    "יצירה, עריכה, הפעלה ומחיקה של ההתראות המתוזמנות של החברה",
  MANAGE_AUTOMATIONS:
    "בנייה, עריכה, הפעלה, השבתה והרצת בדיקה של האוטומציות של החברה",
};

/**
 * What a MANAGER holds automatically, because a trainer does and a manager sits above one. Mirrors
 * `PermissionService.TRAINER_BASELINE` on the server, which is where it is enforced.
 *
 * <p>These are deliberately NOT checkboxes: offering a toggle that cannot turn anything off is worse
 * than offering nothing, because it reads as a decision the admin gets to make. The picker shows them
 * as a read-only note instead, so the admin can still see what the persona starts with.
 */
export const BASELINE_CAPABILITIES: readonly Capability[] = [
  CAPABILITIES.MANAGE_EXERCISES,
  CAPABILITIES.MANAGE_TEMPLATES,
  CAPABILITIES.MANAGE_SUPPLEMENTS,
  CAPABILITIES.MANAGE_FORMS,
  CAPABILITIES.MANAGE_MENU_TEMPLATES,
  CAPABILITIES.MANAGE_FOOD,
];

export const ALL_CAPABILITIES: Capability[] = Object.values(CAPABILITIES);

/**
 * The checkboxes an admin actually decides — everything a trainer canNOT already do.
 *
 * <p>Derived from {@link BASELINE_CAPABILITIES} rather than listed again, so the two can never
 * disagree: adding a constant to the baseline removes its checkbox in the same edit.
 */
export const GRANTABLE_CAPABILITIES: Capability[] = ALL_CAPABILITIES.filter(
  (capability) => !BASELINE_CAPABILITIES.includes(capability)
);
